if (!customElements.get('gift-bags')) {
  class GiftBags extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context || 'pdp';
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.track = this.querySelector('[data-gift-bags-track]');
      this.prevBtn = this.querySelector('[data-gift-bags-prev]');
      this.nextBtn = this.querySelector('[data-gift-bags-next]');
      this.footer = this.querySelector('[data-gift-bags-footer]');
      this.countEl = this.querySelector('[data-gift-bags-count]');
      this.totalEl = this.querySelector('[data-gift-bags-total]');
      this.busy = false;

      this.onClick = this.handleClick.bind(this);
      this.onScroll = this.updateNav.bind(this);
      this.onDrawerOpen = this.scheduleNavUpdate.bind(this);
      this.addEventListener('click', this.onClick);
      if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      document.addEventListener('on:cart-drawer:after-open', this.onDrawerOpen);

      if (typeof ResizeObserver === 'function' && this.track) {
        this.resizeObserver = new ResizeObserver(() => this.updateNav());
        this.resizeObserver.observe(this.track);
        if (this.track.parentElement) this.resizeObserver.observe(this.track.parentElement);
      }

      this.scheduleNavUpdate();
      this.updateFooter();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('on:cart-drawer:after-open', this.onDrawerOpen);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    scheduleNavUpdate() {
      requestAnimationFrame(() => {
        this.updateNav();
        requestAnimationFrame(() => this.updateNav());
      });
      setTimeout(() => this.updateNav(), 350);
    }

    /**
     * Pending bags to add with the main product (PDP only).
     * @returns {Array<{id: number, quantity: number}>}
     */
    getPendingItems() {
      return Array.from(this.querySelectorAll('[data-gift-bags-card]'))
        .map((card) => ({
          id: Number(card.dataset.variantId),
          quantity: Number(card.dataset.quantity) || 0
        }))
        .filter((item) => item.id && item.quantity > 0);
    }

    reset() {
      this.querySelectorAll('[data-gift-bags-card]').forEach((card) => {
        this.applyQty(card, 0);
      });
    }

    handleClick(evt) {
      const prevBtn = evt.target.closest('[data-gift-bags-prev]');
      const nextBtn = evt.target.closest('[data-gift-bags-next]');
      if (prevBtn) {
        evt.preventDefault();
        evt.stopPropagation();
        this.scrollByCard(-1);
        return;
      }
      if (nextBtn) {
        evt.preventDefault();
        evt.stopPropagation();
        this.scrollByCard(1);
        return;
      }

      const card = evt.target.closest('[data-gift-bags-card]');
      if (!card || this.busy) return;

      if (evt.target.closest('[data-gift-bags-add]')) {
        evt.preventDefault();
        evt.stopPropagation();
        this.setQty(card, 1);
        return;
      }

      if (evt.target.closest('[data-gift-bags-plus]')) {
        evt.preventDefault();
        evt.stopPropagation();
        this.setQty(card, (Number(card.dataset.quantity) || 0) + 1);
        return;
      }

      if (evt.target.closest('[data-gift-bags-minus]')) {
        evt.preventDefault();
        evt.stopPropagation();
        this.setQty(card, (Number(card.dataset.quantity) || 0) - 1);
      }
    }

    getMaxQty(card) {
      if (card.dataset.inventoryPolicy === 'continue') return 999;
      const inventory = Number(card.dataset.inventory);
      const current = Number(card.dataset.quantity) || 0;
      if (Number.isFinite(inventory) && inventory > 0) return Math.max(inventory, current);
      return 999;
    }

    async setQty(card, nextQty) {
      const qty = Math.max(0, Math.min(this.getMaxQty(card), nextQty));
      const previousQty = Number(card.dataset.quantity) || 0;

      if (this.context !== 'minicart') {
        this.applyQty(card, qty);
        return;
      }

      const variantId = Number(card.dataset.variantId);
      if (qty === previousQty || !variantId) return;

      this.busy = true;
      this.classList.add('is-busy');
      this.applyQty(card, qty);

      try {
        if (previousQty === 0 && qty > 0) {
          const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
          const oldCartData = await oldCartResponse.json();
          const maxDistinct = Number(theme.settings.cartMaxDistinctItems) || 10;
          const alreadyInCart = oldCartData.items.some((item) => item.variant_id === variantId);

          if (!alreadyInCart && oldCartData.items.length >= maxDistinct) {
            this.applyQty(card, previousQty);
            GiftBags.showDistinctItemsLimitAlert(maxDistinct);
            return;
          }

          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: qty }]
            })
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || response.status);
          }
        } else {
          const response = await fetch(theme.routes.cartChange, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              id: card.dataset.lineKey || String(variantId),
              quantity: qty
            })
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || data.errors || response.status);
          }
        }

        await GiftBags.refreshCartDrawer();
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.applyQty(card, previousQty);
      } finally {
        this.busy = false;
        this.classList.remove('is-busy');
      }
    }

    applyQty(card, qty) {
      card.dataset.quantity = String(qty);
      const qtyEl = card.querySelector('[data-gift-bags-qty]');
      const addBtn = card.querySelector('[data-gift-bags-add]');
      const stepper = card.querySelector('[data-gift-bags-stepper]');

      if (qtyEl) qtyEl.textContent = String(qty);
      if (addBtn) addBtn.hidden = qty > 0;
      if (stepper) stepper.hidden = qty === 0;
      this.updateFooter();
    }

    updateFooter() {
      if (!this.footer) return;

      let count = 0;
      let total = 0;
      this.querySelectorAll('[data-gift-bags-card]').forEach((card) => {
        const qty = Number(card.dataset.quantity) || 0;
        const price = Number(card.dataset.price) || 0;
        count += qty;
        total += qty * price;
      });

      this.footer.hidden = count === 0;
      if (this.countEl) {
        const template = count === 1 ? this.dataset.countOne : this.dataset.countOther;
        this.countEl.textContent = (template || '').replace('[count]', String(count));
      }
      if (this.totalEl) this.totalEl.textContent = GiftBags.formatMoney(total, this.moneyFormat);
    }

    scrollByCard(direction) {
      if (!this.track) return;
      const card = this.track.querySelector('[data-gift-bags-card]');
      const styles = getComputedStyle(this.track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 10;
      const amount = card ? card.getBoundingClientRect().width + gap : 212;
      this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      setTimeout(() => this.updateNav(), 320);
    }

    updateNav() {
      if (!this.track || !this.prevBtn || !this.nextBtn) return;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= maxScroll - 2;
      const canScroll = maxScroll > 2;

      this.prevBtn.disabled = !canScroll || atStart;
      this.nextBtn.disabled = !canScroll || atEnd;
    }

    static async refreshCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh(true);
        return;
      }

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) return;
      const iconResponse = await fetch('?sections=cart-icon-bubble');
      const sections = await iconResponse.json();
      if (sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = sections['cart-icon-bubble'];
      }
    }

    static showDistinctItemsLimitAlert(maxDistinct) {
      const cartDrawer = document.querySelector('cart-drawer');
      const root = cartDrawer || document;
      const alertEl = root.querySelector('#cart-distinct-items-error')
        || document.getElementById('cart-distinct-items-error');
      if (!alertEl) return;

      const body = alertEl.querySelector('.js-quantity-limit-body');
      const template = theme.strings.distinctItemsLimitBodyHtml;
      if (body && template) {
        body.innerHTML = template.replace(/\[quantity\]/g, String(maxDistinct));
      }

      alertEl.dataset.quantityMax = String(maxDistinct);
      alertEl.hidden = false;
      if (cartDrawer && cartDrawer.getAttribute('open') === null) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      }
      alertEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    /**
     * Formats a price in cents using a Shopify money format string.
     * @param {number} cents - Price in cents.
     * @param {string} format - Shopify money format.
     * @returns {string}
     */
    static formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = Number(cents) || 0;
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision = 2, thousands = '.', decimal = ',') {
        if (Number.isNaN(number) || number == null) return '0';
        const numStr = (number / 100).toFixed(precision);
        const parts = numStr.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const centsPart = parts[1] ? decimal + parts[1] : '';
        return dollars + centsPart;
      }

      let formatted;
      switch (formatString.match(placeholderRegex)?.[1]) {
        case 'amount_no_decimals':
          formatted = formatWithDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = formatWithDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = formatWithDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = formatWithDelimiters(value, 2, "'", '.');
          break;
        default:
          formatted = formatWithDelimiters(value, 2, '.', ',');
      }

      return formatString.replace(placeholderRegex, formatted);
    }
  }

  customElements.define('gift-bags', GiftBags);
}
